/**
 * Drawing freehand. §6.4.
 *
 * Press, draw, let go. What was drawn becomes a handful of smooth curves - see
 * shapes/fit.ts - stored as the same points the pen makes, so a pencil line can
 * be reshaped point by point afterwards like anything else.
 *
 * Finishing near where it started closes the shape, as with the pen. Escape
 * while drawing gives the stroke up.
 *
 * Like the pen, the stroke is kept here until it is finished: a half-drawn
 * line is not the customer's yet, and undo should not have to know about it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Group, Line, Rect } from 'react-konva';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { mmToPx, pxToMm } from '../model/units';
import { fitCurves, toNodes } from '../shapes/fit';
import { addDrawn } from '../state/actions';

const ACCENT = '#dd1d26';

/** Screen pixels: how closely the curves must follow the hand. */
const FOLLOW = 2;

/** Finishing within this many pixels of the start closes the shape. */
const CLOSE_NEAR = 10;

/** A stroke shorter than this was a click, and draws nothing. */
const TOO_SHORT = 4;

export function PencilTool( { zoom, onDone }: { zoom: number; onDone: () => void } ) {
  const view = useEditor( ( s ) => s.view );

  /** The stroke so far, in millimetres on the page. */
  const [ stroke, setStroke ] = useState<Array<{ x: number; y: number }>>( [] );
  const drawing = useRef<Array<{ x: number; y: number }> | null>( null );

  useEffect( () => {
    const key = ( event: KeyboardEvent ) => {
      if ( event.key !== 'Escape' ) return;

      // The pencil's own, like the pen's: Escape here is not "deselect".
      event.preventDefault();
      event.stopImmediatePropagation();

      drawing.current = null;
      setStroke( [] );
      onDone();
    };

    window.addEventListener( 'keydown', key, true );

    return () => window.removeEventListener( 'keydown', key, true );
  }, [ onDone ] );

  const where = ( event: Konva.KonvaEventObject<MouseEvent> ) => {
    const point = event.target.getStage()?.getPointerPosition();

    return point ? { x: pxToMm( point.x - view.x, zoom ), y: pxToMm( point.y - view.y, zoom ) } : null;
  };

  const onDown = useCallback(
    ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      if ( event.evt.button !== 0 ) return;

      const point = where( event );

      if ( ! point ) return;

      drawing.current = [ point ];
      setStroke( [ point ] );
    },
    [ view.x, view.y, zoom ] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onMove = useCallback(
    ( event: Konva.KonvaEventObject<MouseEvent> ) => {
      const points = drawing.current;
      const point = where( event );

      if ( ! points || ! point ) return;

      const last = points[ points.length - 1 ];

      // Half a pixel of movement is the mouse reporting that it has not moved.
      if ( last && mmToPx( Math.hypot( point.x - last.x, point.y - last.y ), zoom ) < 0.5 ) return;

      points.push( point );
      setStroke( points.slice() );
    },
    [ view.x, view.y, zoom ] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onUp = useCallback( () => {
    const points = drawing.current;

    drawing.current = null;
    setStroke( [] );

    if ( ! points || points.length < 2 ) return;

    const first = points[ 0 ] as { x: number; y: number };
    const last = points[ points.length - 1 ] as { x: number; y: number };
    const span = points.reduce( ( most, p ) => Math.max( most, mmToPx( Math.hypot( p.x - first.x, p.y - first.y ), zoom ) ), 0 );

    if ( span < TOO_SHORT ) return;

    // Back at the start, having gone somewhere: a closed shape. The last point
    // is put exactly on the first, so the curve meets itself.
    const closed = points.length > 8 && span > CLOSE_NEAR * 2 && mmToPx( Math.hypot( last.x - first.x, last.y - first.y ), zoom ) < CLOSE_NEAR;

    if ( closed ) points[ points.length - 1 ] = { ...first };

    // Fitted to every point the mouse reported, not a thinned set: the fit
    // measures its error against what it is given, so thinning first let the
    // curve stray from points it never saw.
    const curves = fitCurves( points, pxToMm( FOLLOW, zoom ) );
    const nodes = toNodes( curves, closed );

    if ( nodes.length >= 2 ) addDrawn( nodes, closed );

    onDone();
  }, [ zoom, onDone ] );

  return (
    <Group>
      {/* The same sheet as the pen: the pencil draws on paper and off it. */}
      <Rect
        x={ -1e5 }
        y={ -1e5 }
        width={ 2e5 }
        height={ 2e5 }
        onMouseDown={ onDown }
        onMouseMove={ onMove }
        onMouseUp={ onUp }
      />

      { stroke.length > 1 && (
        <Line
          x={ view.x }
          y={ view.y }
          points={ stroke.flatMap( ( p ) => [ mmToPx( p.x, zoom ), mmToPx( p.y, zoom ) ] ) }
          stroke={ ACCENT }
          strokeWidth={ 1.5 }
          lineCap="round"
          lineJoin="round"
          listening={ false }
        />
      ) }
    </Group>
  );
}
